import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, EmptyState, Skeleton } from "@/components/Primitives";
import { fmtMoney, fmtDate } from "@/lib/format";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import MonthlyContactDialog from "@/components/MonthlyContactDialog";
import { Megaphone, Phone, Mail, CalendarClock, ShieldCheck, ClipboardCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import BulkActions from "@/components/BulkActions";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdvertiserOverview from "@/components/advertising/AdvertiserOverview";
import ZoneBrowser from "@/components/advertising/ZoneBrowser";

const daysUntil = (d) => (d ? Math.floor((new Date(d) - Date.now()) / 86400000) : null);
const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d)) / 86400000) : null);

// Rolling 30-day monthly-contact rule: Current <=21d, Due 22-30d, Overdue >30d.
function contactStatus(age) {
  if (age == null) return { key: "none", label: "No contact logged", level: "red" };
  if (age > 30) return { key: "overdue", label: `Overdue (${age}d)`, level: "red" };
  if (age > 21) return { key: "due", label: `Due (${age}d)`, level: "yellow" };
  return { key: "current", label: `Current (${age}d ago)`, level: "green" };
}

// GREEN / YELLOW / RED account health with an explicit reason; monthly contact feeds in automatically.
function health(b, renewal) {
  const until = daysUntil(renewal?.renewal_date);
  const cs = contactStatus(daysSince(b.last_contacted));
  if (until != null && until < 0) return { level: "red", label: "Needs attention", reason: "Renewal overdue" };
  if (b.payment_status === "Overdue") return { level: "red", label: "Needs attention", reason: "Payment overdue" };
  if (cs.level === "red") return { level: "red", label: "Needs attention", reason: cs.key === "none" ? "No monthly contact logged" : "Monthly contact overdue" };
  if (until != null && until <= 60) return { level: "yellow", label: "Monitor", reason: "Renewal approaching" };
  if (cs.level === "yellow") return { level: "yellow", label: "Monitor", reason: "Monthly contact due" };
  return { level: "green", label: "Healthy", reason: "Contract active · contact current" };
}
const DOT = { green: "#9ACC32", yellow: "#F59E0B", red: "#FE421C" };
const BG = { green: "bg-[#9ACC32]/12 text-[#5B880C]", yellow: "bg-[#F59E0B]/12 text-[#B45309]", red: "bg-[#FE421C]/12 text-[#C42607]" };

function AccountHealth() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [renewals, setRenewals] = useState({});
  const [loading, setLoading] = useState(true);
  const [logBiz, setLogBiz] = useState(null);
  const { user } = useAuth();
  const canDelete = !["sales_rep", "readonly"].includes(user?.role);
  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/businesses?lifecycle=Advertiser"), api.get("/renewals").catch(() => ({ data: { items: [] } }))])
      .then(([b, r]) => {
        setRows(b.data.items);
        const map = {};
        (r.data.items || []).forEach((x) => { map[x.business_id] = x; });
        setRenewals(map);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = rows.reduce((a, b) => { a[health(b, renewals[b.id]).level]++; return a; }, { green: 0, yellow: 0, red: 0 });

  return (
    <div data-testid="adv-health">
      <div className="flex justify-end mb-3">
        <div className="flex gap-2">
          {["green", "yellow", "red"].map((k) => (
            <div key={k} className="flex items-center gap-1.5 rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-sm" data-testid={`adv-count-${k}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: DOT[k] }} />
              <span className="tnum font-semibold text-slate-700">{counts[k]}</span>
            </div>
          ))}
        </div>
      </div>

      {canDelete && rows.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <Checkbox checked={rows.length > 0 && rows.every((b) => sel.has(b.id))} onCheckedChange={(v) => setSel(v ? new Set(rows.map((b) => b.id)) : new Set())} data-testid="select-all" />
          <span className="text-sm text-slate-500">Select all advertisers</span>
        </div>
      )}
      {canDelete && <BulkActions endpoint="businesses" ids={[...sel]} singular="advertiser" title="Advertisers" onDone={load} clear={() => setSel(new Set())} />}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-60" />)}</div>
      ) : rows.length === 0 ? (
        <div className="cc-card"><EmptyState icon={Megaphone} title="No active advertisers" hint="Win an opportunity to activate a business as an advertiser." /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((b) => {
            const h = health(b, renewals[b.id]);
            const rd = renewals[b.id]?.renewal_date;
            const cs = contactStatus(daysSince(b.last_contacted));
            return (
              <div key={b.id} className="cc-card cc-card-hover p-5 flex flex-col" data-testid={`adv-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    {canDelete && <div onClick={(e) => e.stopPropagation()} className="pt-0.5"><Checkbox checked={sel.has(b.id)} onCheckedChange={() => toggle(b.id)} data-testid={`select-${b.id}`} /></div>}
                  <button onClick={() => navigate(`/businesses/${b.id}`)} className="text-left min-w-0">
                    <h3 className="font-display font-semibold text-slate-800 truncate hover:text-[#0094A8]">{b.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{[b.city, b.territory].filter(Boolean).join(" · ")}</p>
                  </button>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${BG[h.level]}`} data-testid={`adv-health-${b.id}`}>
                    <span className="h-2 w-2 rounded-full" style={{ background: DOT[h.level] }} />{h.label}
                  </span>
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />{h.reason}</p>

                <div className="mt-4 grid grid-cols-2 gap-y-2 text-xs">
                  <div><p className="text-slate-400">Value</p><p className="font-bold text-[#0094A8] tnum">{fmtMoney(b.potential_value)}</p></div>
                  <div><p className="text-slate-400">Renewal</p><p className="text-slate-700 tnum flex items-center gap-1"><CalendarClock className="h-3 w-3 text-slate-300" />{rd ? fmtDate(rd) : "—"}</p></div>
                  <div className="col-span-2"><p className="text-slate-400">Monthly contact</p>
                    <p className={`font-medium ${cs.level === "red" ? "text-[#C42607]" : cs.level === "yellow" ? "text-[#B45309]" : "text-[#5B880C]"}`} data-testid={`adv-contact-status-${b.id}`}>
                      {cs.label}
                      {b.last_contact_method ? ` · ${b.last_contact_method}` : ""}
                      {b.last_contact_by_name ? ` · ${b.last_contact_by_name}` : ""}
                    </p>
                    {b.next_follow_up && <p className="text-[11px] text-slate-400 mt-0.5">Next follow-up {fmtDate(b.next_follow_up)}</p>}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setLogBiz(b)} data-testid={`adv-log-contact-${b.id}`}>
                    <ClipboardCheck className="h-3 w-3" />Log Contact
                  </Button>
                  {b.phone && <Button size="sm" variant="outline" className="h-7 text-xs" asChild><a href={`tel:${b.phone}`}><Phone className="h-3 w-3" /></a></Button>}
                  {b.email && <Button size="sm" variant="outline" className="h-7 text-xs" asChild><a href={`mailto:${b.email}`}><Mail className="h-3 w-3" /></a></Button>}
                  <Button size="sm" className="h-7 text-xs" onClick={() => navigate(`/businesses/${b.id}`)} data-testid={`adv-open-${b.id}`}>Open</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <MonthlyContactDialog
        open={!!logBiz}
        onOpenChange={(o) => !o && setLogBiz(null)}
        businessId={logBiz?.id}
        businessName={logBiz?.name}
        onSaved={load}
      />
    </div>
  );
}

export default function Advertisers() {
  const { user } = useAuth();
  const isManager = ["tenant_owner", "platform_owner", "sales_manager"].includes(user?.role);
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState(isManager ? "overview" : "zones");
  const [zoneKey, setZoneKey] = useState(0);
  const navigate = useNavigate();
  useEffect(() => { api.get("/advertising/overview").then((r) => setOv(r.data)); }, []);
  const openZone = (z) => { navigate(`/advertisers?market=${z.market_id}&zone=${z.id}`, { replace: true }); setZoneKey((k) => k + 1); setTab("zones"); };
  return (
    <div data-testid="page-advertisers">
      <PageHeader title="Advertisers" subtitle="One master record per company — placements by market and zone, contract status and renewals." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {isManager && <TabsTrigger value="overview" data-testid="adv-tab-overview">Owner Overview</TabsTrigger>}
          <TabsTrigger value="zones" data-testid="adv-tab-zones">By Market &amp; Zone</TabsTrigger>
          <TabsTrigger value="health" data-testid="adv-tab-health">Account Health</TabsTrigger>
        </TabsList>
        {isManager && <TabsContent value="overview" className="mt-4"><AdvertiserOverview data={ov} onOpenZone={openZone} /></TabsContent>}
        <TabsContent value="zones" className="mt-4"><ZoneBrowser key={zoneKey} overview={ov} /></TabsContent>
        <TabsContent value="health" className="mt-4"><AccountHealth /></TabsContent>
      </Tabs>
    </div>
  );
}
